import { Platform } from "react-native";
import { safeAsyncStorage } from "@utils/safeAsyncStorage";

/**
 * Bridges the one unrecoverable gap in the web→app hand-off.
 *
 * Exchanging a hand-off token CONSUMES it server-side (single use, by design).
 * If the app dies between that call returning and Firebase persisting the
 * session, the user is stranded: the hand-off token is spent, so every retry
 * 401s, and the claim link that produced it is spent too. That is not
 * theoretical — a startup OTA reload on the first launch after install did
 * exactly this in production.
 *
 * So the custom token is written here BEFORE sign-in and removed only after it
 * succeeds; a later launch finds it and completes the sign-in without touching
 * the (now dead) hand-off token. Firebase custom tokens expire in about an
 * hour and only mint a session for the user they were issued for, so a
 * leftover stash is inert rather than a standing credential.
 *
 * Web has no stash: AsyncStorage is a native-only module there (mocked out in
 * metro.config.js, so a call throws), and the gap it bridges is the native
 * install-then-OTA-reload one; a web tab that dies mid sign-in simply reloads
 * the link.
 */
const STORAGE_KEY = "auth:pendingHandoffCustomToken";
const enabled = Platform.OS !== "web";

export async function stashCustomToken(customToken: string): Promise<void> {
  if (!enabled) return;
  await safeAsyncStorage.setItem(STORAGE_KEY, customToken);
}

export async function readStashedCustomToken(): Promise<string | null> {
  if (!enabled) return null;
  return safeAsyncStorage.getItem(STORAGE_KEY);
}

export async function clearStashedCustomToken(): Promise<void> {
  if (!enabled) return;
  await safeAsyncStorage.removeItem(STORAGE_KEY);
}
